import type { ComponentProps, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
// radix/themes
import { Heading } from '@radix-ui/themes';

// local components
import { HubertButton } from 'components/HubertUI/HubertButton';
import HubertCheckbox from 'components/HubertUI/HubertCheckbox';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import LanguageCombobox from 'components/comboboxes/LanguageCombobox';
import HubertPopover from 'components/HubertUI/HubertPopover';

import { useAddQuestionLanguageMutation } from 'rtk/query/apis/questions';
import IsAxiosError from 'components/common/IsAxiosError';
import { type TAvailableLanguageCodes, QUESTION_PERMISSIONS } from '@hubertai/screening-question-types';
import HubertLabel from 'components/HubertUI/HubertLabel';
import { useUser } from 'hooks/useUser';
import { isRecruiterUser, userHasPermission, userHasResourcePermission } from 'types/user.types';

interface Props extends ComponentProps<typeof HubertPopover> {
  questionId: string;
  existingLanguages: TAvailableLanguageCodes[];
  initialFromLanguage: TAvailableLanguageCodes;
  initialToLanguage?: TAvailableLanguageCodes;
  disabled?: boolean;
  triggerText?: string | ReactNode;
  contentContainer?: HTMLElement;
}

const AddQuestionLanguage = ({
  questionId,
  existingLanguages,
  initialFromLanguage,
  initialToLanguage,
  triggerText,
  disabled = false,
  contentContainer,
  ...props
}: Props) => {
  const { t } = useTranslation('Questionsv2');
  const user = useUser();
  const [languageFrom, setLanguageFrom] = useState<TAvailableLanguageCodes>(initialFromLanguage);
  const [languageTo, setLanguageTo] = useState<TAvailableLanguageCodes>();
  const [autoTranslate, setAutoTranslate] = useState<boolean>(true);

  useEffect(() => {
    setLanguageFrom(initialFromLanguage);
  }, [initialFromLanguage]);

  useEffect(() => {
    if (initialToLanguage) {
      setLanguageTo(initialToLanguage);
    }
  }, [initialToLanguage]);

  const [addQuestionLanguageRequest, { isLoading, isError, error }] = useAddQuestionLanguageMutation();

  const container = contentContainer ?? (document.getElementById('main-content') as HTMLElement);

  if (
    isRecruiterUser(user) &&
    !userHasResourcePermission(user, QUESTION_PERMISSIONS.Update, questionId, 'question') &&
    !userHasPermission(user, QUESTION_PERMISSIONS.Update)
  ) {
    return null;
  }

  return (
    <HubertPopover {...props}>
      <HubertPopover.Trigger asChild>
        <HubertButton size={'xs'} variant={'destructive'} disabled={disabled}>
          {triggerText ?? t('addLanguage.triggerText')}
        </HubertButton>
      </HubertPopover.Trigger>
      <HubertPopover.Content
        className={clsx('container flex flex-col justify-around', 'p-[18px]')}
        portalContent={true}
        portalElement={container}
      >
        <Heading>{t('addLanguage.heading')}</Heading>

        <div className="flex flex-col justify-around gap-4">
          <div className="hubert-vertical-center flex flex-row justify-between gap-2">
            {t('addLanguage.baseLanguagePrompt')}
            <LanguageCombobox
              onValueChange={(value) => {
                setLanguageFrom(value as TAvailableLanguageCodes);
              }}
              value={languageFrom}
              allowLanguages={existingLanguages}
            />
          </div>

          <div className="hubert-vertical-center flex flex-row justify-between gap-2">
            {t('addLanguage.targetLanguagePrompt')}
            <LanguageCombobox
              onValueChange={(value) => {
                setLanguageTo(value as TAvailableLanguageCodes);
              }}
              value={languageTo}
              excludeLanguages={existingLanguages}
            />
          </div>

          {languageTo && languageTo.length && languageFrom && languageFrom.length ? (
            <div className="hubert-vertical-center flex flex-row gap-4">
              <HubertCheckbox
                checked={autoTranslate}
                onCheckedChange={(e: boolean) => {
                  setAutoTranslate(e);
                }}
              />
              {/* TODO(i18n): Category 4 — "Auto translate from X to Y?" has inline badges splitting the sentence */}
              <HubertLabel>
                Auto translate from
                <HubertBadge variant={'secondary'}>{languageFrom}</HubertBadge>
                to
                <HubertBadge variant={'secondary'}>{languageTo}</HubertBadge>?
              </HubertLabel>
            </div>
          ) : null}

          {isError ? <IsAxiosError error={error} /> : null}

          <div className="hubert-vertical-center flex flex-row justify-between gap-4">
            <div className={clsx(isLoading ? 'text-danger-500' : 'hidden')}>{t('addLanguage.pleaseWait')}</div>

            <div></div>

            <HubertButton
              disabled={
                !questionId.length || !languageFrom || !languageFrom.length || !languageTo || !languageTo.length
                  ? true
                  : false
              }
              className="justify-end"
              size={'sm'}
              loading={isLoading}
              onClick={() => {
                const cond =
                  !questionId.length || !languageFrom || !languageFrom.length || !languageTo || !languageTo.length;
                if (cond) {
                  toast.error(t('addLanguage.toast.missingValues'));
                  return;
                }

                addQuestionLanguageRequest({
                  questionId,
                  languageFrom,
                  languageTo,
                  autoTranslate,
                })
                  .unwrap()
                  .then(async (result: any) => {
                    console.log('result', result);
                    if (result.question) {
                      toast.success(t('addLanguage.toast.copySuccess', { from: languageFrom, to: languageTo }));
                    } else {
                      toast.error(`${result.message}`);
                    }
                  })
                  .catch((rejected: any) => {
                    // TODO(i18n): Category 4 — template literal embeds dynamic status/statusText/message; 'Unknown error.' fallback cannot be extracted independently
                    toast.error(`${rejected.status} ${rejected.statusText}: ${rejected.message ?? 'Unknown error.'}`);
                  });
              }}
            >
              {t('addLanguage.addButton')}
            </HubertButton>
          </div>
        </div>
      </HubertPopover.Content>
    </HubertPopover>
  );
};

export default AddQuestionLanguage;
